import { PageShell } from "@superadmin/components/PageShell";
import { orpc } from "@superadmin/utils/orpc.tanstack.client";
import { useQuery } from "@tanstack/react-query";
import { LoadingSpinner } from "@voicely/ui-mui/components/LoadingSpinner";
import { Chip } from "@voicely/ui-mui/data-display/Chip";
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from "@voicely/ui-mui/data-display/Table";
import { Alert } from "@voicely/ui-mui/feedback/Alert";
import { TextField } from "@voicely/ui-mui/form/TextField";
import { Paper } from "@voicely/ui-mui/layout/Paper";
import { Stack } from "@voicely/ui-mui/layout/Stack";
import { useUrlState } from "@voicely/web-shared/use_url_state";
import { z } from "zod";

/**
 * Which settings actually reached the running backend — presence, length and
 * a fingerprint, never a value. Answers "the deploy pane says it is set,
 * so why does the feature fail closed" without SSH.
 */
export const ContainerEnvPage = () => {
	// The filter is a committed search, not a keystroke stream: it lands in
	// the URL on Enter/blur via the TextField's defaultValue+onBlur pair, so a
	// pasted link reproduces the same filtered view.
	const [filter, setFilter] = useUrlState("q", z.string(), "");

	const envQuery = useQuery(orpc.superAdmin.containerEnv.queryOptions({ input: {} }));

	const toolbar = (
		<TextField
			size="small"
			label="Filter by name"
			defaultValue={filter}
			onBlur={(event) => setFilter(event.target.value)}
			onKeyDown={(event) => {
				if (event.key === "Enter") setFilter((event.target as HTMLInputElement).value);
			}}
			sx={{ minWidth: 260 }}
		/>
	);

	if (envQuery.isPending) {
		return (
			<PageShell title="Container env" toolbar={toolbar}>
				<LoadingSpinner />
			</PageShell>
		);
	}

	if (envQuery.error) {
		return (
			<PageShell title="Container env" toolbar={toolbar}>
				<Alert severity="error">{envQuery.error.message}</Alert>
			</PageShell>
		);
	}

	const needle = filter.trim().toUpperCase();
	const rows = envQuery.data.checked.filter(
		(entry) => needle === "" || entry.name.toUpperCase().includes(needle),
	);

	return (
		<PageShell
			title="Container env"
			subtitle="What reached the running backend process. Never the values."
			toolbar={toolbar}
		>
			<Stack spacing={2}>
				<TableContainer component={Paper} variant="outlined">
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Name</TableCell>
								<TableCell>Present</TableCell>
								<TableCell align="right">Length</TableCell>
								<TableCell>Fingerprint</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{rows.map((entry) => (
								<TableRow key={entry.name}>
									<TableCell sx={{ fontFamily: "monospace", fontSize: 13 }}>{entry.name}</TableCell>
									<TableCell>
										<Chip
											label={entry.present ? "set" : "unset"}
											size="small"
											color={entry.present ? "success" : "default"}
										/>
									</TableCell>
									<TableCell align="right">{entry.length ?? "—"}</TableCell>
									<TableCell sx={{ fontFamily: "monospace", fontSize: 13 }}>
										{entry.fingerprint ?? "—"}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			</Stack>
		</PageShell>
	);
};
